import { getEvaluationPeriodsSystem } from 'app/crud/evaluationPeriods.crud';
import i18next from 'app/translations/i18n';
import { put, select, takeLatest } from 'redux-saga/effects';
import { REDUCER_EVALUATION_PERIODS } from '../conf';

export const actionTypes = {
  GetEvaluationPeriods: 'evaluationPeriods/GET_EVALUATION_PERIODS',
  SetEvaluationPeriods: 'evaluationPeriods/SET_EVALUATION_PERIODS',
  SetEvaluationPeriodsError: 'evaluationPeriods/SET_EVALUATION_PERIODS_ERROR',
  SetCacheHit: 'evaluationPeriods/SET_CACHE_HIT',
  ClearCache: 'evaluationPeriods/CLEAR_CACHE',
};

const initialState = {
  loading: false,
  data: [],
  evalutationPeriod: [],
  periods: [],
  error: null,
  lastFetch: null,
  cacheExpiry: 5 * 60 * 1000, // 5 minutos en milisegundos
};

// Sin persistencia: `evalutationPeriod`/`periods` se derivan al reducir (con nombres
// traducidos) y no se rehidratan, así que un `lastFetch` rehidratado daría la caché
// por válida con los selectores vacíos.
export function reducer(state = initialState, action) {
  switch (action.type) {
    case actionTypes.GetEvaluationPeriods: {
      return { ...state, loading: true, error: null };
    }

    case actionTypes.SetEvaluationPeriods: {
      return {
        ...state,
        loading: false,
        data: action.payload,
        evalutationPeriod: getUniqueEvaluationPeriods(action.payload),
        periods: getPeriods(action.payload),
        error: null,
        lastFetch: Date.now(),
      };
    }

    case actionTypes.SetEvaluationPeriodsError: {
      return {
        ...state,
        loading: false,
        error: action.payload,
        lastFetch: Date.now(),
      };
    }

    case actionTypes.SetCacheHit: {
      return { ...state, loading: false, error: null };
    }

    case actionTypes.ClearCache: {
      return {
        ...state,
        data: [],
        lastFetch: null,
        error: null,
      };
    }

    default:
      return state;
  }
}

export const selectors = {
  getLoading: (state) => {
    return state.entities?.[REDUCER_EVALUATION_PERIODS] ? state.entities[REDUCER_EVALUATION_PERIODS].loading : false;
  },
  getEvaluationPeriods: (state) => {
    return state.entities?.[REDUCER_EVALUATION_PERIODS] ? state.entities[REDUCER_EVALUATION_PERIODS].evalutationPeriod : [];
  },
  getPeriods: (state) => {
    return state.entities?.[REDUCER_EVALUATION_PERIODS] ? state.entities[REDUCER_EVALUATION_PERIODS].periods : [];
  },
  getError: (state) => {
    return state.entities?.[REDUCER_EVALUATION_PERIODS] ? state.entities[REDUCER_EVALUATION_PERIODS].error : null;
  },
  getLastFetch: (state) => {
    return state.entities?.[REDUCER_EVALUATION_PERIODS] ? state.entities[REDUCER_EVALUATION_PERIODS].lastFetch : null;
  },
  getCacheExpiry: (state) => {
    return state.entities?.[REDUCER_EVALUATION_PERIODS] ? state.entities[REDUCER_EVALUATION_PERIODS].cacheExpiry : 5 * 60 * 1000;
  },
  isCacheValid: (state) => {
    const lastFetch = selectors.getLastFetch(state);
    const cacheExpiry = selectors.getCacheExpiry(state);

    // Una respuesta vacía es un resultado válido y cacheable: exigir datos aquí
    // deja shouldFetch en true para siempre y encadena peticiones sin fin.
    if (!lastFetch) return false;

    return Date.now() - lastFetch < cacheExpiry;
  },
  shouldFetch: (state) => {
    const isLoading = selectors.getLoading(state);
    const isCacheValid = selectors.isCacheValid(state);

    return !isLoading && !isCacheValid;
  },
};

export const actions = {
  getEvaluationPeriods: (forceRefresh = false) => ({
    type: actionTypes.GetEvaluationPeriods,
    payload: { forceRefresh },
  }),
  setEvaluationPeriods: (data) => ({
    type: actionTypes.SetEvaluationPeriods,
    payload: data,
  }),
  setEvaluationPeriodsError: (error) => ({
    type: actionTypes.SetEvaluationPeriodsError,
    payload: error,
  }),
  setCacheHit: () => ({
    type: actionTypes.SetCacheHit,
  }),
  clearCache: () => ({
    type: actionTypes.ClearCache,
  }),
};

export function* saga() {
  yield takeLatest(actionTypes.GetEvaluationPeriods, function* getEvaluationPeriodsSaga(action) {
    try {
      const { forceRefresh } = action.payload;

      // Si no es un refresh forzado, verificar cache
      if (!forceRefresh) {
        const isCacheValid = yield select(selectors.isCacheValid);

        // El estado ya contiene los datos derivados: basta con cerrar el loading.
        if (isCacheValid) {
          yield put(actions.setCacheHit());
          return;
        }
      }

      // Hacer llamada a la API
      const { data } = yield getEvaluationPeriodsSystem();
      if (data?.data) {
        yield put(actions.setEvaluationPeriods(data.data));
      } else {
        yield put(actions.setEvaluationPeriodsError('Error al cargar períodos evaluativos'));
      }
    } catch (error) {
      yield put(actions.setEvaluationPeriodsError(error.message || 'Error al cargar períodos evaluativos'));
    }
  });
}

function getUniqueEvaluationPeriods(data) {
  // Agrupar por evaluation_period y mantener solo los únicos
  const groupedData = data
    .map((item) => ({
      guid: item.evaluation_period,
      name: i18next.t(`dates:${item.evaluation_period}`),
    }))
    .reduce((acc, item) => {
      if (!acc.has(item.guid)) {
        acc.set(item.guid, item);
      }
      return acc;
    }, new Map());

  // Convertir Map a array y ordenar alfabéticamente por name
  return Array.from(groupedData.values()).sort((a, b) => a.name.localeCompare(b.name));
}

function getPeriods(data) {
  return data.map((item) => ({
    guid: item.id,
    evaluation_period: item.evaluation_period,
    name: i18next.t(`dates:${item.evaluation_period}_x`, { number: Number.parseInt(item.id.replace(/[^0-9]/g, '')) }),
  }));
}
